/**
 * affSymbology — single source of truth for AFF (Aircraft Flight Following)
 * marker icons: a PNG per aircraft type + status, served by the backend. Shared
 * by the map layer (AffLayerProvider) and the legend (customLegends) so they
 * cannot drift.
 */
import { getBackendUrl } from '../../../services/configService';

export type AircraftStatus = 'flying' | 'landed';

export interface AircraftType {
  /** Internal code used in the icon filename (rw/bd/tk/cl/fb). */
  code: string;
  /** Human-readable label for the legend. */
  label: string;
}

/** Aircraft types in display order (matches getAircraftType in AffLayerProvider). */
export const AIRCRAFT_TYPES: AircraftType[] = [
  { code: 'RW', label: 'Helicopter' },
  { code: 'BD', label: 'Bird Dog' },
  { code: 'TK', label: 'Air Tanker' },
  { code: 'CL', label: 'Scooper (CL-series)' },
  { code: 'FB', label: 'Fire Boss (AT-802A)' },
];

/** Full URL of the marker PNG for an aircraft type code + status. */
export function affIconUrl(code: string, status: AircraftStatus): string {
  return `${getBackendUrl()}/aff/${code.toLowerCase()}_${status}.png`;
}

/**
 * Full URL of the glTF model for an aircraft type code (#820).
 *
 * Deliberately keyed on the SAME five type codes as the icons, so one aircraft
 * type resolves to one icon and one model. A type gaining an icon but not a
 * model would draw in 2D and vanish in 3D.
 *
 * There is no status in the filename, unlike the icons: status is carried by
 * colour instead, which one model can do and a second file would duplicate.
 */
export function affModelUrl(code: string): string {
  return `${getBackendUrl()}/aff/models/${code.toLowerCase()}.glb`;
}

/**
 * Model colours by status (#820).
 *
 * Yellow flying, black landed — the same distinction the separate flying and
 * landed PNGs carry in 2D, expressed as colour because a model layer can be
 * tinted per feature and a second model file would only duplicate geometry.
 *
 * The pair separates by LUMINANCE rather than hue, so it reads for anyone
 * regardless of colour vision. A red/green pair would not, and is the reason
 * the fire status work in #653 uses dark greens.
 *
 * This also resolves an inconsistency in the model set itself: rw and bd carry
 * materials, while tk, cl and fb came out of trimesh as bare geometry with
 * none. Drawn at full colour mix they all render the same way.
 */
export const MODEL_COLOUR_FLYING = '#ffd400';
export const MODEL_COLOUR_LANDED = '#111111';

export function modelColourFor(status: AircraftStatus): string {
  return status === 'flying' ? MODEL_COLOUR_FLYING : MODEL_COLOUR_LANDED;
}
